<template>
  <div class="billing-payments">
    <div class="billing-payments__scroll-container q-pa-sm q-px-md">
      <!-- Subscription Plan Section -->
      <div class="billing-payments__section-wrapper">
        <div class="billing-payments__section-header">
          <h3 class="billing-payments__section-title">Subscription Plan</h3>
          <p class="billing-payments__section-subtitle">Manage your subscription and billing cycle</p>
        </div>
        <div class="billing-payments__section-body">
          <!-- Section-specific loading state -->
          <div v-if="isPlansLoading" class="billing-payments__section-loading">
            <q-spinner-dots color="primary" size="40px" />
          </div>
          <div v-else>
            <keep-alive>
              <SubscriptionPlan :show-plans="showAvailablePlans" @toggle-plans="toggleAvailablePlans" @unsubscribe="handleUnsubscribe" />
            </keep-alive>

            <transition name="slide-down">
              <div v-if="showAvailablePlans" class="billing-payments__available-plans">
                <AvailablePlans :current-plan-id="currentPlanId" :is-enterprise-contact-loading="isEnterpriseContactLoading" @select-plan="handleSelectPlan" @contact="handleEnterpriseContact" />
              </div>
            </transition>
          </div>
        </div>
      </div>

      <!-- Payment Methods Section -->
      <div class="billing-payments__section-wrapper">
        <div class="billing-payments__section-header">
          <h3 class="billing-payments__section-title">Payment Methods</h3>
          <p class="billing-payments__section-subtitle">Manage your payment methods and billing information</p>
        </div>
        <div class="billing-payments__section-body">
          <!-- Section-specific loading state -->
          <div v-if="isPaymentsLoading" class="billing-payments__section-loading">
            <q-spinner-dots color="primary" size="40px" />
          </div>
          <div v-else>
            <keep-alive>
              <PaymentMethodsList @set-default="confirmSetDefaultPaymentMethod" @remove="confirmRemovePaymentMethod" @open-add="openPaymentDialog" />
            </keep-alive>
          </div>
        </div>
      </div>

      <!-- Billing History Section with its own loading state handled internally -->
      <keep-alive>
        <BillingHistory ref="billingHistory" :is-loading="isHistoryLoading" />
      </keep-alive>
    </div>

    <!-- Payment Method Dialog -->
    <PaymentMethodDialog v-model="showPaymentDialog" :loading="isPaymentMethodLoading" :error="error" :full-name="fullName" @update:fullName="val => (fullName = val)" @confirm="handleSubmit" />

    <!-- Payment Success Dialog -->
    <PaymentSuccessDialog v-model="showSuccessDialog" @close="closePaymentSuccess" />

    <!-- Upgrade Plan Dialog -->
    <UpgradeConfirmDialog v-if="selectedPlan" v-model="showUpgradeDialog" :plan="selectedPlan" :loading="isUpgradeLoading" @confirm="confirmUpgrade" @cancel="cancelUpgrade" />

    <!-- Upgrade Success Dialog -->
    <UpgradeSuccessDialog v-model="showUpgradeSuccessDialog" @close="closeUpgradeSuccess" />

    <!-- Unsubscribe dialogs -->
    <UnsubscribeConfirmDialog v-if="selectedPlan" v-model="showUnsubscribeDialog" :plan="selectedPlan" :loading="isUnsubscribeLoading" @confirm="confirmUnsubscribe" @cancel="cancelUnsubscribe" />

    <UnsubscribeSuccessDialog v-model="showUnsubscribeSuccessDialog" @close="closeUnsubscribeSuccess" />

    <!-- Business Contact Dialog -->
    <BusinessContactDialog v-model="showBusinessContactDialog" @close="closeBusinessContact" />

    <!-- Confirm Remove Payment Method Dialog -->
    <RemovePaymentMethodDialog v-model="showRemoveDialog" :payment-method="methodToRemove" :loading="isPaymentMethodLoading" @confirm="removePaymentMethod" @cancel="cancelRemovePaymentMethod" />

    <!-- Confirm Set Default Payment Method Dialog -->
    <SetDefaultPaymentMethodDialog
      v-model="showDefaultDialog"
      :payment-method="methodToSetDefault"
      :loading="isPaymentMethodLoading"
      @confirm="setDefaultPaymentMethod"
      @cancel="cancelSetDefaultPaymentMethod"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { PaymentMethodApi, SettingsApi } from '@api/index';
import SubscriptionPlan from './BillingPayments/SubscriptionPlan.vue';
import AvailablePlans from './BillingPayments/AvailablePlans.vue';
import UpgradeConfirmDialog from './BillingPayments/UpgradeConfirmDialog.vue';
import UpgradeSuccessDialog from './BillingPayments/UpgradeSuccessDialog.vue';
import UnsubscribeConfirmDialog from './BillingPayments/UnsubscribeConfirmDialog.vue';
import UnsubscribeSuccessDialog from './BillingPayments/UnsubscribeSuccessDialog.vue';
import BillingHistory from './BillingPayments/BillingHistory.vue';
import PaymentSuccessDialog from './BillingPayments/PaymentSuccessDialog.vue';
import RemovePaymentMethodDialog from './BillingPayments/RemovePaymentMethodDialog.vue';
import SetDefaultPaymentMethodDialog from './BillingPayments/SetDefaultPaymentMethodDialog.vue';
import BusinessContactDialog from './BillingPayments/BusinessContactDialog.vue';
import PaymentMethodsList from './BillingPayments/PaymentMethodsList.vue';
import PaymentMethodDialog from './BillingPayments/PaymentMethodDialog.vue';

export default {
  name: 'BillingPayments',
  components: {
    SubscriptionPlan,
    AvailablePlans,
    UpgradeConfirmDialog,
    UpgradeSuccessDialog,
    UnsubscribeConfirmDialog,
    UnsubscribeSuccessDialog,
    BillingHistory,
    PaymentSuccessDialog,
    RemovePaymentMethodDialog,
    SetDefaultPaymentMethodDialog,
    BusinessContactDialog,
    PaymentMethodsList,
    PaymentMethodDialog
  },
  data() {
    return {
      // Stripe related
      stripe: null,
      elements: null,
      card: null,
      clientSecret: '',

      // UI dialog states
      showAvailablePlans: false,
      showPaymentDialog: false,
      showSuccessDialog: false,
      showUpgradeDialog: false,
      showUpgradeSuccessDialog: false,
      showBusinessContactDialog: false,
      showUnsubscribeDialog: false,
      showUnsubscribeSuccessDialog: false,
      showRemoveDialog: false,
      showDefaultDialog: false,

      // Form data
      fullName: '',

      // Selected items for dialogs
      selectedPlan: null,
      methodToRemove: null,
      methodToSetDefault: null,

      // Section-specific loading states
      isPlansLoading: true,
      isPaymentsLoading: true,
      isHistoryLoading: true,
      isSettingsLoading: false,

      // Add a flag to prevent state reset during navigation
      isNavigatingAway: false,

      // Add a flag to track if component is ready
      isComponentMounted: false,

      // Loading state for enterprise contact request
      isEnterpriseContactLoading: false
    };
  },
  computed: {
    ...mapGetters({
      settings: 'settings/getSettings',
      user: 'user/getUser',
      currentPlan: 'billing/currentPlan',
      availablePlans: 'billing/availablePlans',
      paymentMethods: 'billing/paymentMethods',
      error: 'billing/error',
      isUpgradeLoading: 'billing/isUpgradeLoading',
      isUnsubscribeLoading: 'billing/isUnsubscribeLoading',
      isPaymentMethodLoading: 'billing/isPaymentMethodLoading'
    }),
    stripePublic() {
      return this.settings && this.settings.stripe_public ? this.settings.stripe_public : null;
    },
    currentPlanId() {
      return this.currentPlan ? this.currentPlan.id : null;
    }
  },
  created() {
    // Initial section-specific loading states
    this.isPlansLoading = true;
    this.isPaymentsLoading = true;
    this.isHistoryLoading = true;
    this.isSettingsLoading = true;

    // Load settings first as they're needed for other functionality
    this.loadSettings();

    // Load each section independently with slight staggered timing for better UI
    setTimeout(() => this.loadSubscriptionPlans(), 50);
    setTimeout(() => this.loadPaymentMethods(), 100);
    setTimeout(() => this.loadBillingHistory(), 150);

    // Initialize Stripe in the background as it's not needed immediately
    this.initializeStripeSetupIntent();
  },
  watch: {
    showPaymentDialog(val) {
      // When the dialog opens, we need to mount the card element
      if (val) {
        this.$nextTick(() => {
          // Need to re-mount when dialog opens
          if (this.card) {
            this.card.mount('#card-element');
          }
        });
      } else {
        // Clear form when dialog closes
        this.fullName = '';
        if (this.card) {
          this.card.clear();
        }
      }
    },

    // Add a watcher for stripePublic to initialize Stripe when it becomes available
    stripePublic(val) {
      if (val) {
        this.initializeStripe();
      }
    },

    // Add a watcher for the error state from the store
    error(newError) {
      if (newError) {
        // Display error notification
        this.$q.notify({
          color: 'negative',
          message: newError,
          position: 'top',
          timeout: 3000
        });
      }
    }
  },
  async mounted() {
    // Set component mounted state
    this.isComponentMounted = true;

    // Initialize Stripe if the public key is already available
    if (this.stripePublic) {
      this.initializeStripe();
    }
  },
  methods: {
    ...mapActions({
      fetchSubscriptionPlans: 'billing/fetchSubscriptionPlans',
      fetchPaymentMethods: 'billing/fetchPaymentMethods',
      fetchBillingHistory: 'billing/fetchBillingHistory',
      subscribeToPlan: 'billing/subscribeToPlan',
      cancelSubscription: 'billing/cancelSubscription',
      addPaymentMethod: 'billing/addPaymentMethod',
      removePaymentMethodAction: 'billing/removePaymentMethod',
      setDefaultPaymentMethodAction: 'billing/setDefaultPaymentMethod',
      clearError: 'billing/clearError',
      createSetupIntent: 'billing/createSetupIntent'
    }),

    // Add a dedicated method for Stripe initialization
    initializeStripe() {
      try {
        console.log('Initializing Stripe with key:', this.stripePublic);
        // Initialize Stripe
        this.stripe = window.Stripe(this.stripePublic);

        // Create card element
        this.elements = this.stripe.elements({ locale: 'en' });
        this.card = this.elements.create('card', {
          style: {
            base: {
              fontSize: '14px',
              color: 'rgba(0, 0, 0, 0.87)',
              fontFamily: 'Inter, sans-serif',
              '::placeholder': {
                color: 'rgba(0, 0, 0, 0.6)'
              }
            },
            invalid: {
              color: '#f44336'
            }
          }
        });

        // Add event listeners to monitor card status
        this.card.addEventListener('change', event => {
          // Clear any existing errors
          if (event.error) {
            this.$root.$emit('stripe:card-error', event.error.message);
          } else {
            this.$root.$emit('stripe:card-error', '');
          }

          // Update the complete status
          this.$root.$emit('stripe:card-complete', event.complete);
        });
      } catch (error) {
        console.error('Error initializing payment form:', error);
      }
    },

    // Refreshes each section independently when tab is revisited
    async fetchData() {
      // Only show loading states briefly to prevent jarring UI
      const showLoading = false;

      if (showLoading) {
        this.isPlansLoading = true;
        this.isPaymentsLoading = true;
        this.isHistoryLoading = true;
      }

      try {
        // Load each section in parallel but handle them independently
        await Promise.all([this.loadSubscriptionPlans(showLoading), this.loadPaymentMethods(showLoading), this.loadBillingHistory(showLoading)]);
      } catch (error) {
        // Silent fail for tab switches
      }
    },

    // Helper methods for loading different sections
    // Loading methods that coordinate between API calls and component UI state
    async loadSettings() {
      this.isSettingsLoading = true;
      try {
        if (!this.settings || Object.keys(this.settings).length === 0) {
          const settings = await SettingsApi.settings();
          await this.$store.dispatch('settings/updateSettings', settings.data);
        }
      } catch (error) {
        this.$q.notify({
          type: 'negative',
          message: 'Failed to load application settings',
          position: 'top'
        });
      } finally {
        this.isSettingsLoading = false;
      }
    },

    // Optimized loading methods with consistent error handling pattern
    async loadSubscriptionPlans(showLoading = true) {
      if (showLoading) this.isPlansLoading = true;
      try {
        await this.fetchSubscriptionPlans();
      } catch (error) {
        // Error already handled in the store action
      } finally {
        this.isPlansLoading = false;
      }
    },

    async loadPaymentMethods(showLoading = true) {
      if (showLoading) this.isPaymentsLoading = true;
      try {
        await this.fetchPaymentMethods();
      } catch (error) {
        // Error already handled in the store action
      } finally {
        this.isPaymentsLoading = false;
      }
    },

    async loadBillingHistory(showLoading = true) {
      if (showLoading) this.isHistoryLoading = true;
      try {
        await this.fetchBillingHistory();
      } catch (error) {
        // Error already handled in the store action
      } finally {
        this.isHistoryLoading = false;
      }
    },

    // Now uses the store action for better centralization of API logic
    async initializeStripeSetupIntent() {
      if (this.stripePublic) {
        const clientSecret = await this.createSetupIntent();
        if (clientSecret) {
          this.clientSecret = clientSecret;
        }
      }
    },

    // UI state management
    toggleAvailablePlans() {
      this.showAvailablePlans = !this.showAvailablePlans;
    },

    // Payment method handling
    async openPaymentDialog() {
      // Fetch SetupIntent client secret when dialog is opened
      try {
        const response = await PaymentMethodApi.createSetupIntent();
        this.clientSecret = response.data.client_secret;
      } catch (error) {
        console.error('Failed to initialize payment form:', error);
      }

      this.showPaymentDialog = true;
    },

    async handleSubmit() {
      try {
        await this.addPaymentMethod({
          fullName: this.fullName,
          stripe: this.stripe,
          card: this.card,
          clientSecret: this.clientSecret
        });

        // Close the dialog and show success message
        this.showPaymentDialog = false;
        this.showSuccessDialog = true;

        // Clear form
        this.fullName = '';
        this.card.clear();
      } catch (error) {
        console.error('Error adding payment method:', error);
      }
    },

    confirmSetDefaultPaymentMethod(method) {
      this.methodToSetDefault = method;
      this.showDefaultDialog = true;
    },

    async setDefaultPaymentMethod() {
      if (!this.methodToSetDefault || !this.methodToSetDefault.id) {
        return;
      }

      try {
        const success = await this.setDefaultPaymentMethodAction(this.methodToSetDefault.id);
        if (success) {
          this.showDefaultDialog = false;
          this.methodToSetDefault = null;

          this.$q.notify({
            color: 'positive',
            message: 'Default payment method updated successfully',
            position: 'top',
            timeout: 2000
          });
        }
      } catch (error) {
        console.error('Error setting default payment method:', error);
        // Error notification will be handled by the watcher
      }
    },

    confirmRemovePaymentMethod(method) {
      this.methodToRemove = method;
      this.showRemoveDialog = true;
    },

    async removePaymentMethod() {
      if (!this.methodToRemove || !this.methodToRemove.id) {
        return;
      }

      try {
        const success = await this.removePaymentMethodAction(this.methodToRemove.id);
        if (success) {
          this.showRemoveDialog = false;
          this.methodToRemove = null;

          this.$q.notify({
            color: 'positive',
            message: 'Payment method removed successfully',
            position: 'top',
            timeout: 2000
          });
        }
      } catch (error) {
        console.error('Error removing payment method:', error);
        // Error notification will be handled by the watcher
      }
    },

    // Subscription plan handling
    handleSelectPlan(plan) {
      this.selectedPlan = plan;

      // Check if user has payment methods before showing upgrade dialog
      if (!this.paymentMethods || this.paymentMethods.length === 0) {
        // Store the selected plan and show payment method dialog
        this.openPaymentDialog();

        // Add a message to notify the user why they're seeing the payment form
        this.$q.notify({
          color: 'info',
          message: 'Please add a payment method before subscribing to a plan',
          position: 'top',
          timeout: 3000
        });
      } else {
        // User has payment methods, show upgrade dialog
        this.showUpgradeDialog = true;
      }
    },

    async confirmUpgrade() {
      try {
        const success = await this.subscribeToPlan({
          planId: this.selectedPlan.id,
          stripe: this.stripe
        });

        if (success) {
          // Close dialogs and show success
          this.showUpgradeDialog = false;
          this.showUpgradeSuccessDialog = true;
          this.showAvailablePlans = false;

          // Refresh billing history to show the new invoice immediately
          this.$nextTick(() => {
            if (this.$refs.billingHistory) {
              this.$refs.billingHistory.refreshBillingHistory();
            }
          });
        }
      } catch (error) {
        console.error('Error upgrading plan:', error);
        // Error notification will be handled by the watcher
      }
    },

    cancelUpgrade() {
      this.selectedPlan = null;
      this.showUpgradeDialog = false;
      this.clearError();
    },

    // Unsubscribe handling
    handleUnsubscribe(plan) {
      this.selectedPlan = plan;
      this.showUnsubscribeDialog = true;
    },

    async confirmUnsubscribe() {
      try {
        const success = await this.cancelSubscription();

        if (success) {
          // Close dialogs and show success
          this.showUnsubscribeDialog = false;
          this.showUnsubscribeSuccessDialog = true;
        }
      } catch (error) {
        console.error('Error unsubscribing from plan:', error);
        // Error notification will be handled by the watcher
      }
    },

    // Dialog close handlers
    closePaymentSuccess() {
      this.showSuccessDialog = false;
      this.clearError();

      // If user was trying to subscribe to a plan, continue with the upgrade process
      if (this.selectedPlan) {
        this.showUpgradeDialog = true;
      }
    },

    closeUpgradeSuccess() {
      this.showUpgradeSuccessDialog = false;
      this.clearError();

      // Refresh only when needed (after an actual upgrade)
      this.fetchSubscriptionPlans(); // Only refresh plans, not everything
    },

    cancelUnsubscribe() {
      this.showUnsubscribeDialog = false;
      this.selectedPlan = null;
      this.clearError();
    },

    closeUnsubscribeSuccess() {
      this.showUnsubscribeSuccessDialog = false;
      this.clearError();

      // Only refresh plan data after unsubscribing
      this.fetchSubscriptionPlans();
    },

    /**
     * Handle enterprise contact request
     * Sends API request to notify admins and shows success dialog
     */
    async handleEnterpriseContact() {
      try {
        // Show loading state
        this.isEnterpriseContactLoading = true;

        // Call the API to send the contact request
        const response = await this.$store.dispatch('billing/requestEnterpriseContact');

        // Check if the response indicates success
        if (response && response.data && response.data.success) {
          // Show success dialog
          this.showBusinessContactDialog = true;
        } else {
          // Handle unexpected response format
          console.warn('Unexpected response format:', response);
          this.showBusinessContactDialog = true; // Still show dialog as fallback
        }
      } catch (error) {
        // Handle error - only show one notification
        this.$q.notify({
          color: 'negative',
          message: 'Failed to send enterprise contact request. Please try again or contact us directly.',
          position: 'top',
          timeout: 3000
        });
        console.error('Error requesting enterprise contact:', error);
      } finally {
        // Reset loading state
        this.isEnterpriseContactLoading = false;
      }
    },

    closeBusinessContact() {
      this.showBusinessContactDialog = false;
    },

    cancelRemovePaymentMethod() {
      this.showRemoveDialog = false;
      this.methodToRemove = null;
      this.clearError();
    },

    cancelSetDefaultPaymentMethod() {
      this.showDefaultDialog = false;
      this.methodToSetDefault = null;
      this.clearError();
    },

    closePaymentDialog() {
      this.showPaymentDialog = false;
      this.clearError();

      // Check if payment methods were added and continue with upgrade if needed
      if (this.paymentMethods.length > 0 && this.selectedPlan) {
        this.showUpgradeDialog = true;
      }
    }
  },

  beforeRouteEnter(to, from, next) {
    // This hook is called before the component is created
    // Use it to determine if we're coming from another tab or from outside
    next(vm => {
      // Check if we're coming from UserDropdown (different route)
      const isExternalNavigation = !from.name || !from.name.includes('profile');

      if (isExternalNavigation) {
        // Coming from outside, let's set a brief loading state
        vm.isPlansLoading = true;
        vm.isPaymentsLoading = true;
        vm.isHistoryLoading = true;

        // Force loading UI to render before loading data
        vm.$nextTick(() => {
          // Start loading data in sequence with small delays
          setTimeout(() => vm.loadSubscriptionPlans(), 100);
          setTimeout(() => vm.loadPaymentMethods(), 150);
          setTimeout(() => vm.loadBillingHistory(), 200);
        });
      }
    });
  },

  // Add hook to prevent state resets during navigation
  beforeRouteLeave(to, from, next) {
    this.isNavigatingAway = true;
    next();
  },

  // Add hook to clear errors when component is destroyed
  beforeDestroy() {
    // Only clear error if we're not navigating between tabs
    if (!this.isNavigatingAway) {
      this.clearError();
    }

    // Make sure any dialogs are properly closed
    this.showPaymentDialog = false;
    this.showSuccessDialog = false;
    this.showUpgradeDialog = false;
    this.showUpgradeSuccessDialog = false;
    this.showUnsubscribeDialog = false;
    this.showUnsubscribeSuccessDialog = false;
    this.showRemoveDialog = false;
    this.showDefaultDialog = false;
    this.showBusinessContactDialog = false;
  }
};
</script>

<style lang="scss" scoped>
@import './BillingPayments/billing-payments-styles.scss';

.billing-payments {
  width: 100%;
  height: 100%;
  text-align: left;
  display: flex;
  flex-direction: column;
}
</style>
